import { useMemo, useState } from "react";
import Select from "react-dropdown-select";
import "./Dropdown.scss";
import { DropdownProps, Option } from "./Dropdown.types";

export const Dropdown: React.FC<DropdownProps> = ({
  disabled = false,
  required = false,
  options = [],
  onChange,
  searchable = false,
  selectedOption,
  selectedMultiOptions,
  label,
  multi,
  onCreateNew,
  onChangeMultiple,
  autoFocus,
  onClose,
  clearable,
  defaultMenuIsOpen,
  smaller,
  minWidth,
}) => {
  const [searchValue, setSearchValue] = useState<string>();

  /**
   * Holds the dropdown options to display
   * includes the passed options, filters them and adds an entry to createif necessary
   */
  const localOptions: Option[] = useMemo((): Option[] => {
    if ((!searchValue || searchValue === "") && !onCreateNew) return options;
    const optionsToShow: Option[] = [];
    if (searchValue)
      optionsToShow.push(
        ...options.filter(
          (option) =>
            option.label
              ?.toString()
              .toLowerCase()
              .includes(searchValue.toLowerCase()) ||
            option.value
              ?.toString()
              .toLowerCase()
              .includes(searchValue.toLowerCase()),
        ),
      );
    else optionsToShow.push(...options);

    if (onCreateNew && searchValue) {
      optionsToShow.push({
        label: searchValue,
        value: `CREATE_ENTRY${searchValue}`,
      });
    }
    return optionsToShow;
  }, [onCreateNew, options, searchValue]);

  /**
   * Sets the selected option or options on the first render, after that
   * the <Select/> keeps track on the changed states itself
   */
  const selectedOptionFromOptions: Option[] = useMemo(
    (): Option[] =>
      multi
        ? options.filter((option) =>
            selectedMultiOptions?.includes(option.value),
          )
        : options.find((option) => option.value === selectedOption)
          ? [options.find((option) => option.value === selectedOption)!]
          : [],

    [multi, options, selectedMultiOptions, selectedOption],
  );

  /**
   * Helper method to call the correct onChange for the dropdown
   * @param selectedOption The selected options provided by the onChange method in the <Select/> tag
   */
  const handleChange = (selectedOptions: Option[]): void => {
    if (options.length === 0) return;
    if (onChangeMultiple)
      onChangeMultiple(selectedOptions.map((value) => value.value));
    else if (
      selectedOptions[0]?.value &&
      selectedOptions.length === 1 &&
      onChange
    ) {
      if (
        onCreateNew &&
        selectedOptions[0].value === `CREATE_ENTRY${searchValue}` &&
        searchValue
      ) {
        onCreateNew({ label: searchValue, value: searchValue });
        setSearchValue("");
      } else onChange(selectedOptions[0].value);
    } else if (onChange) onChange("");
  };

  return (
    <div
      className="dropdown-component__wrapper"
      data-testid="dropdownComponent"
    >
      {label && (
        <p
          data-testid="dropdown-label"
          className={["dropdown-component__label"].join(" ")}
        >
          {label}
        </p>
      )}
      <Select
        style={{ minWidth: minWidth ?? "" }}
        clearable={clearable}
        data-testid="drop"
        required={required}
        disabled={disabled}
        values={selectedOptionFromOptions}
        autoFocus={autoFocus}
        onDropdownClose={onClose}
        defaultMenuIsOpen={defaultMenuIsOpen}
        options={localOptions}
        dropdownPosition="bottom"
        selectAll={multi}
        onSelectAll={() =>
          onChangeMultiple &&
          onChangeMultiple(options.flatMap((option) => option.value))
        }
        onClearAll={() => onChangeMultiple && onChangeMultiple([])}
        //Called when an option is selected, with the new selected options
        onSelect={(value) => handleChange(value as Option[])}
        //Called when an option is deselected, with the new selected options
        onDeselect={(value) => handleChange(value as Option[])}
        //OnChange is triggered, without userInput everytime the internal component state gets updated, not recommended to use because of onChange being executed right after the component mounts
        onChange={() => {}}
        searchable={searchable}
        className={[
          "dropdown-component__select",
          selectedOptionFromOptions.length > 0 ? "with-value" : "",
          searchable ? "searchable" : "non-searchable",
          smaller ? "smaller" : "",
        ].join(" ")}
        dropdownGap={-5}
        multi={multi}
      />
    </div>
  );
};
